/**
 * New deal form (PRD §10.2 v1.2 + §8.1 v1.2 either-or-both parents).
 *
 * v1.2 collapsed the v1.1 "exactly one parent" rule: a deal can now
 * carry a contact, an account, or both, with the obvious invariant
 * that — when both are set — the contact must actually belong to that
 * account. The form reflects that with two side-by-side pickers, both
 * shown at once, both optional individually, at least one required:
 *
 *   - Contact: dropdown + "+" inline composer for quick-adding a new
 *     person. Once a contact is picked, its account auto-fills the
 *     account picker and the picker locks (clearing the contact
 *     unlocks it again).
 *   - Account: AccountPicker with inline create. While only the
 *     account is set, the contact dropdown filters to that account's
 *     people (you can't pair a contact with an account they don't
 *     belong to — the server would 422 ``contact_account_mismatch``,
 *     so we pre-empt the bad shape client-side).
 *
 * Three deep-link entry points:
 *
 *   1. ``/deals/new`` — empty form.
 *   2. ``/deals/new?contact=<id>`` — contact pre-selected (and the
 *      account auto-fills if that contact has one).
 *   3. ``/deals/new?account=<id>`` — account pre-selected; contact
 *      dropdown filters to that account's people.
 *
 * v1.2 niceties carried over: existing-open / existing-closed conflicts
 * are soft warnings (with a link to the existing deal + a "create
 * another anyway" override), the assignee picker is privileged-only,
 * and toasts narrate success.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { Briefcase, Loader2, Plus, UserPlus } from "lucide-react";
import { type FormEvent, useEffect, useMemo, useState } from "react";

import { BackLink } from "../components/BackLink";

import { AccountPicker } from "../components/AccountPicker";
import { ContactPicker } from "../components/ContactPicker";
import {
  ApiError,
  type DealCreate,
  accountsApi,
  contactsApi,
  dealsApi,
  isDealConflictDetail,
  isDuplicatePhoneDetail,
  usersApi,
} from "../lib/api";
import { useAuthStore } from "../lib/auth";
import { usePicklistOptions } from "../lib/picklists";
import { toast } from "../lib/toast";

interface NewDealSearch {
  contact?: string;
  account?: string;
}

export const Route = createFileRoute("/_app/deals/new")({
  validateSearch: (search: Record<string, unknown>): NewDealSearch => {
    // exactOptionalPropertyTypes is on — only include the keys when
    // they're real strings; never `{ contact: undefined }`.
    const out: NewDealSearch = {};
    if (typeof search.contact === "string") out.contact = search.contact;
    if (typeof search.account === "string") out.account = search.account;
    return out;
  },
  component: NewDealPage,
});

interface OpenWarning {
  message: string;
  /** UUID of the existing deal — null means "we know one exists but you
   *  can't see it" (the hidden 409 variant). */
  existingDealId: string | null;
  closed: boolean;
}

function NewDealPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const { contact: presetContactId, account: presetAccountId } = useSearch({
    from: "/_app/deals/new",
  });

  // Parent state — both optional, at least one required at submit.
  const [contactId, setContactId] = useState(presetContactId ?? "");
  const [accountId, setAccountId] = useState<string | null>(presetAccountId ?? null);

  // Shared deal fields. ``value`` is a picklist string — defaults to
  // "medium" (the seed) and can be any active picklist row.
  const [value, setValue] = useState<string>("medium");
  const [requirements, setRequirements] = useState("");
  const [notes, setNotes] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<OpenWarning | null>(null);
  const [confirming, setConfirming] = useState(false);

  const isPrivileged = user?.role === "admin" || user?.role === "manager";

  const contactsQ = useQuery({
    queryKey: ["contacts", { limit: 200, offset: 0 }],
    queryFn: () => contactsApi.list({ limit: 200, offset: 0 }),
  });
  const accountsQ = useQuery({
    queryKey: ["accounts", { limit: 200 }],
    queryFn: () => accountsApi.list({ limit: 200 }),
  });
  const usersQ = useQuery({
    queryKey: ["users", { active_only: true }],
    queryFn: () => usersApi.list({ active_only: true }),
    enabled: isPrivileged,
  });

  // If the URL pre-selects a contact that isn't in our visible list, we
  // still want it to appear in the dropdown so we don't silently lose it.
  const allContacts = useMemo(() => {
    const list = contactsQ.data?.items ?? [];
    if (presetContactId && !list.some((c) => c.id === presetContactId)) {
      return [
        {
          id: presetContactId,
          name: `(unknown — ${presetContactId.slice(0, 8)}…)`,
          phone: "",
          account_id: null as string | null,
        },
        ...list,
      ];
    }
    return list;
  }, [contactsQ.data, presetContactId]);

  // Selected entities (memoised so the auto-link / filter effects don't
  // re-run on every render).
  const selectedContact = useMemo(
    () => (contactId ? (allContacts.find((c) => c.id === contactId) ?? null) : null),
    [contactId, allContacts],
  );

  // Effect: when the user picks a contact whose account is set, mirror
  // that into the account picker. Picking the contact is the strongest
  // signal — the rep is saying "this person, at the place they work" —
  // so we override any earlier account-only selection.
  useEffect(() => {
    if (!selectedContact) return;
    if (selectedContact.account_id && selectedContact.account_id !== accountId) {
      setAccountId(selectedContact.account_id);
    }
    // If the contact has no account, we DON'T blank an existing
    // accountId selection — the rep may have started by picking the
    // account and is now adding a contact who hasn't been linked yet.
    // The submit-time check below will catch the mismatch.
  }, [selectedContact, accountId]);

  // The contact dropdown is filtered down to contacts of the selected
  // account (if any). When no account is picked, we show everyone.
  // This enforces the v1.2 rule: you can only pair a contact with the
  // account they actually belong to.
  const contactDropdown = useMemo(() => {
    if (!accountId) return allContacts;
    // Always include the currently-selected contact even if they don't
    // match the filter — otherwise picking an account would silently
    // wipe the contact selection from the visible list and confuse
    // the user. The server-side mismatch check is the safety net.
    const filtered = allContacts.filter((c) => c.account_id === accountId || c.id === contactId);
    return filtered;
  }, [accountId, allContacts, contactId]);

  // Hint text when a chosen contact + account don't match — pre-empts
  // the server 422 with a clearer, in-form message.
  const mismatch = useMemo(() => {
    if (!selectedContact || !accountId) return null;
    if (!selectedContact.account_id) {
      return {
        kind: "no_contact_account" as const,
        message:
          "This contact isn't linked to any account yet — link them first, then come back to create the deal.",
        contactId: selectedContact.id,
      };
    }
    if (selectedContact.account_id !== accountId) {
      return {
        kind: "account_mismatch" as const,
        message:
          "This contact belongs to a different account. Pick that account on the right, or pick a different contact.",
        contactsActualAccountId: selectedContact.account_id,
      };
    }
    return null;
  }, [selectedContact, accountId]);

  // Sync presets when they change (back/forward navigation).
  useEffect(() => {
    if (presetContactId) setContactId(presetContactId);
  }, [presetContactId]);
  useEffect(() => {
    if (presetAccountId) setAccountId(presetAccountId);
  }, [presetAccountId]);

  // Submit-eligibility: at least one parent + no obvious mismatch.
  const canSubmit = (Boolean(contactId) || Boolean(accountId)) && mismatch === null;

  const submit = async ({
    confirm = false,
    share = false,
  }: { confirm?: boolean; share?: boolean } = {}) => {
    setError(null);
    if (!confirm) setWarning(null);
    if (confirm) {
      setConfirming(true);
    } else {
      setSubmitting(true);
    }
    try {
      if (!contactId && !accountId) {
        setError("Pick a contact, an account, or both — at least one is required.");
        return;
      }
      const body: DealCreate = {
        contact_id: contactId || null,
        account_id: accountId,
        value,
        requirements: requirements.trim() || null,
        notes: notes.trim() || null,
      };
      if (isPrivileged && assigneeId.trim()) body.assigned_to_id = assigneeId.trim();

      const created = await dealsApi.create(body, { confirm, share });
      queryClient.invalidateQueries({ queryKey: ["deals"] });
      toast.success(confirm ? "Created another deal." : "Deal created.");
      navigate({ to: "/deals/$dealId", params: { dealId: created.id } });
    } catch (err) {
      if (err instanceof ApiError && isDealConflictDetail(err.detail)) {
        const d = err.detail;
        if (
          err.status === 409 &&
          (d.code === "existing_open_deal_visible" || d.code === "existing_open_deal_hidden")
        ) {
          setWarning({
            message: d.message,
            existingDealId: "existing_deal_id" in d ? d.existing_deal_id : null,
            closed: false,
          });
          return;
        }
        if (
          err.status === 409 &&
          (d.code === "existing_closed_deal_visible" || d.code === "existing_closed_deal_hidden")
        ) {
          setWarning({
            message: d.message,
            existingDealId: "existing_deal_id" in d ? d.existing_deal_id : null,
            closed: true,
          });
          return;
        }
        if (err.status === 422 && d.code === "contact_account_mismatch") {
          // Server-side last line of defence. Surface the message.
          setError(d.message);
          return;
        }
        if (err.status === 409 && d.code === "assignee_lacks_account_access") {
          // The chosen assignee can't see the parent account. Ask, then
          // retry granting them access (preserving any duplicate-deal
          // override already confirmed).
          if (window.confirm(d.message)) void submit({ confirm, share: true });
          return;
        }
      }
      const msg = err instanceof ApiError ? err.message : "Could not reach the server.";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
      setConfirming(false);
    }
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    void submit();
  };

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-4 sm:p-6">
      <header className="space-y-1">
        <BackLink fallbackTo="/deals" fallbackLabel="deals" />
        <h1 className="text-2xl font-semibold tracking-tight">New deal</h1>
        <p className="text-sm opacity-60">
          Attach a contact, an account, or both. At least one is required.
        </p>
      </header>

      <form
        onSubmit={onSubmit}
        className="space-y-4 rounded-xl border border-[var(--border)] p-5"
        noValidate
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <ContactField
            value={contactId}
            onChange={(id) => {
              setContactId(id);
              setWarning(null);
            }}
            contacts={contactDropdown}
            onCreated={(c) => {
              queryClient.invalidateQueries({ queryKey: ["contacts"] });
              setContactId(c.id);
            }}
            filteredByAccount={accountId !== null}
          />
          <AccountField
            value={accountId}
            onChange={(next) => {
              setAccountId(next);
              setWarning(null);
            }}
            accountsQ={accountsQ}
            lockedToContactsAccount={Boolean(selectedContact?.account_id)}
          />
        </div>

        {mismatch && (
          <div
            role="alert"
            className="space-y-1 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100"
          >
            <p>{mismatch.message}</p>
            {mismatch.kind === "no_contact_account" && (
              <p className="text-xs">
                <Link
                  to="/contacts/$contactId"
                  params={{ contactId: mismatch.contactId }}
                  className="font-medium underline underline-offset-2 hover:no-underline"
                >
                  Open the contact →
                </Link>
              </p>
            )}
          </div>
        )}

        <Field id="deal-value" label="Deal value">
          <DealValuePicker value={value} onChange={setValue} />
        </Field>

        <Field
          id="deal-requirements"
          label="Requirements"
          hint="Optional. What does the buyer need?"
        >
          <textarea
            id="deal-requirements"
            rows={3}
            value={requirements}
            onChange={(e) => setRequirements(e.target.value)}
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
          />
        </Field>

        <Field
          id="deal-notes"
          label="Internal notes"
          hint="Optional. Visible to whoever can see the deal."
        >
          <textarea
            id="deal-notes"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
          />
        </Field>

        {isPrivileged && (
          <Field
            id="deal-assignee"
            label="Assigned to"
            hint="Manager/Admin only. Leave blank to assign to yourself."
          >
            <select
              id="deal-assignee"
              value={assigneeId}
              onChange={(e) => setAssigneeId(e.target.value)}
              className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
            >
              <option value="">(default — you)</option>
              {usersQ.data?.items.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name || u.email} — {u.role}
                </option>
              ))}
            </select>
          </Field>
        )}

        {warning && (
          <div
            role="alert"
            className="space-y-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100"
          >
            <p>
              <strong>Heads up.</strong> {warning.message}
            </p>
            {warning.existingDealId && (
              <p className="text-xs">
                <Link
                  to="/deals/$dealId"
                  params={{ dealId: warning.existingDealId }}
                  className="font-medium underline underline-offset-2 hover:no-underline"
                >
                  Open the existing deal
                </Link>{" "}
                — or use the button below to create another one anyway.
              </p>
            )}
          </div>
        )}

        {error && (
          <div
            role="alert"
            className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
          >
            {error}
          </div>
        )}

        {/* One primary action. When a warning is showing, the same
         *  button changes label + becomes the override (re-POST with
         *  ?confirm=true). This is also why the warning banner above
         *  doesn't have its own primary button — it would just be a
         *  duplicate of this one. */}
        <button
          type={warning ? "button" : "submit"}
          onClick={warning ? () => void submit({ confirm: true }) : undefined}
          disabled={submitting || confirming || !canSubmit}
          className={`flex w-full items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50 ${
            warning ? "bg-amber-700 dark:bg-amber-600" : "bg-[var(--accent)]"
          }`}
        >
          {submitting || confirming ? (
            <>
              <Loader2 className="size-4 animate-spin" aria-hidden />
              {warning ? "Creating another…" : "Saving…"}
            </>
          ) : warning ? (
            <>
              <Plus className="size-4" aria-hidden />
              {warning.closed ? "Create a new deal anyway" : "Create another deal anyway"}
            </>
          ) : (
            <>
              <Briefcase className="size-4" aria-hidden />
              Create deal
            </>
          )}
        </button>
      </form>
    </div>
  );
}

// ── Contact field ───────────────────────────────────────────────────────────

interface ContactFieldProps {
  value: string;
  onChange: (id: string) => void;
  contacts: ReadonlyArray<{
    id: string;
    name: string;
    phone: string;
    account_id: string | null;
  }>;
  onCreated: (c: { id: string; name: string; phone: string; account_id: string | null }) => void;
  /** True when the dropdown has been narrowed to one account's contacts.
   *  Used only for the hint text. */
  filteredByAccount: boolean;
}

function ContactField({
  value,
  onChange,
  contacts,
  onCreated,
  filteredByAccount,
}: ContactFieldProps) {
  return (
    <Field
      id="deal-contact"
      label="Contact"
      hint={
        filteredByAccount
          ? "Showing only contacts at the selected account."
          : "Optional. Leave blank for an account-only deal."
      }
    >
      <ContactPicker
        id="deal-contact"
        value={value}
        onChange={onChange}
        contacts={contacts}
        placeholder="Pick a contact"
        createComposer={<NewContactInline onCreated={onCreated} />}
      />
    </Field>
  );
}

// ── Account field ───────────────────────────────────────────────────────────

interface AccountFieldProps {
  value: string | null;
  onChange: (next: string | null) => void;
  accountsQ: ReturnType<typeof useQuery<Awaited<ReturnType<typeof accountsApi.list>>>>;
  /** True when an account is currently locked because the chosen
   *  contact is associated with it. The picker remains visible and
   *  clearable, but the hint changes. */
  lockedToContactsAccount: boolean;
}

function AccountField({ value, onChange, accountsQ, lockedToContactsAccount }: AccountFieldProps) {
  return (
    <Field
      id="deal-account"
      label="Account"
      hint={
        lockedToContactsAccount
          ? "Auto-filled from the selected contact. Clear the contact to pick a different account."
          : "Optional. Leave blank for a contact-only deal."
      }
    >
      <AccountPicker
        id="deal-account"
        value={value}
        onChange={onChange}
        accounts={accountsQ.data?.items ?? []}
        loading={accountsQ.isLoading}
        allowCreate
        placeholder="Pick an account"
      />
    </Field>
  );
}

function Field({
  id,
  label,
  hint,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="text-sm">
      <label htmlFor={id} className="mb-1 block opacity-80" id={`${id}-label`}>
        {label}
      </label>
      {children}
      {hint && <span className="mt-1 block text-xs opacity-50">{hint}</span>}
    </div>
  );
}

// ── Inline new-contact composer ───────────────────────────────────────────────

/** Inline mini-form — opens in a popover next to the contact dropdown.
 *  Phone + name are required (matches the standalone /contacts/new
 *  validation). On success we call ``onCreated`` so the parent can
 *  refresh the dropdown and pre-select the new contact.
 *
 *  Layout note: the closed state renders a 38×38 button that sits
 *  alongside the dropdown; the open state renders ``basis-full`` so it
 *  wraps onto a new row inside the parent ``flex flex-wrap`` container.
 */
function NewContactInline({
  onCreated,
}: {
  onCreated: (c: { id: string; name: string; phone: string; account_id: string | null }) => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setName("");
    setPhone("");
    setEmail("");
    setError(null);
  };

  const onAdd = async () => {
    setError(null);
    if (!phone.trim() || !name.trim()) {
      setError("Phone and name are required.");
      return;
    }
    setSubmitting(true);
    try {
      const created = await contactsApi.create({
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim() || null,
      });
      toast.success(`Added contact "${created.name}".`);
      onCreated({
        id: created.id,
        name: created.name,
        phone: created.phone,
        account_id: created.account_id,
      });
      reset();
      setOpen(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && isDuplicatePhoneDetail(err.detail)) {
        if (err.detail.code === "duplicate_phone_visible") {
          // The phone is already on file as a contact we can see —
          // pick that one instead of creating a new dupe.
          toast.info("That phone is already on file — picked the existing contact.");
          onCreated({
            id: err.detail.existing_contact_id,
            name: name.trim() || "(existing contact)",
            phone: err.detail.phone,
            account_id: null,
          });
          reset();
          setOpen(false);
          return;
        }
        setError(err.detail.message);
      } else {
        const msg = err instanceof ApiError ? err.message : "Could not create contact.";
        setError(msg);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Add a new contact"
        aria-label="Add a new contact"
        className="inline-flex size-[38px] shrink-0 items-center justify-center rounded-md border border-[var(--border)] text-[var(--accent)] transition-colors hover:border-[var(--accent)] hover:bg-[var(--accent)]/10 focus-visible:border-[var(--accent)] focus-visible:outline-none"
      >
        <UserPlus className="size-4" aria-hidden />
      </button>
    );
  }

  return (
    <div className="basis-full">
      <div className="space-y-2 rounded-md border border-[var(--accent)]/40 bg-black/5 p-3 dark:bg-white/5">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium opacity-80">Quick-add a contact</p>
          <button
            type="button"
            onClick={() => {
              reset();
              setOpen(false);
            }}
            className="text-xs opacity-60 hover:opacity-100"
          >
            Cancel
          </button>
        </div>
        {/*
          NOT a nested <form> — that would be invalid HTML inside the
          outer New-Deal form. Submit is wired via the button's onClick,
          and Enter inside any of the inputs triggers the same handler
          via onKeyDown so the keyboard UX still feels form-like.
        */}
        <div
          className="space-y-2"
          onKeyDown={(e) => {
            if (e.key === "Enter" && !submitting) {
              e.preventDefault();
              void onAdd();
            }
          }}
        >
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            <input
              type="tel"
              inputMode="tel"
              required
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Phone (e.g. 9876543210)"
              className="rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 font-mono text-sm outline-none focus:border-[var(--accent)]"
            />
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name"
              className="rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
            />
          </div>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Email (optional)"
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-2 py-1.5 text-sm outline-none focus:border-[var(--accent)]"
          />
          {error && (
            <div
              role="alert"
              className="rounded-md border border-red-300 bg-red-50 px-2 py-1 text-xs text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
            >
              {error}
            </div>
          )}
          <button
            type="button"
            onClick={() => void onAdd()}
            disabled={submitting || !phone.trim() || !name.trim()}
            className="inline-flex items-center gap-1 rounded-md bg-[var(--accent)] px-3 py-1.5 text-xs font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? (
              <>
                <Loader2 className="size-3 animate-spin" aria-hidden />
                Adding…
              </>
            ) : (
              <>
                <UserPlus className="size-3" aria-hidden />
                Add contact
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Picklist-driven value picker. Defaults to "medium" the first time
 * the picklist loads if the controlled value isn't in the list (e.g.
 * a manager renamed the seed and we're still on the old value); the
 * picker then re-renders with the matching active option.
 */
function DealValuePicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: string) => void;
}) {
  const { data, isLoading } = usePicklistOptions("deal_value");

  // If the controlled value is no longer active (e.g. picklist
  // changed under us mid-edit), fall through to the first active
  // option. The user sees a deterministic state instead of an empty
  // selection.
  useEffect(() => {
    if (isLoading || data.length === 0) return;
    if (!data.some((o) => o.value === value)) {
      onChange(data[0]!.value);
    }
  }, [data, isLoading, value, onChange]);

  if (isLoading) {
    return (
      <p className="text-xs opacity-60">Loading values…</p>
    );
  }

  if (data.length === 0) {
    return (
      <p className="text-xs text-amber-700 dark:text-amber-300">
        No deal-value options configured. Ask an admin to add some under
        Settings → Picklists.
      </p>
    );
  }

  return (
    <fieldset className="flex flex-wrap gap-2">
      <legend className="sr-only">Deal value</legend>
      {data.map((opt) => {
        const id = `deal-value-${opt.value}`;
        const selected = value === opt.value;
        return (
          <label
            key={opt.id}
            htmlFor={id}
            className={`min-w-20 flex-1 cursor-pointer rounded-md border px-3 py-2 text-center text-sm transition-colors ${
              selected
                ? "border-[var(--accent)] bg-[var(--accent)]/10 font-medium"
                : "border-[var(--border)] hover:bg-black/5 dark:hover:bg-white/5"
            }`}
          >
            <input
              id={id}
              type="radio"
              name="deal-value"
              value={opt.value}
              checked={selected}
              onChange={() => onChange(opt.value)}
              className="sr-only"
            />
            {opt.label}
          </label>
        );
      })}
    </fieldset>
  );
}
